import React, { createContext, useState } from "react";
import MainLayout from "../components/layout/MainLayout";
import { graphql, PageProps } from "gatsby";
import {
  KontentPageMetadata,
  KontentRichText,
  KontentSiteFrame,
} from "../types";
import { SeoProps } from "../components/layout/SEO";
import { HeaderProps } from "../components/siteframe/Header";
import { PageComponents } from "./PageComponents";
import { getMultiChoice } from "../lib/multichoice";
import { getColor } from "../lib/color";
import { useHomePageMetadata } from "../lib/useHomePageMetadata";
import { PageMetadataContext } from "../lib/PageMetadataContext";

interface Props {
  readonly data: PageQueryData;
  readonly pageContext: {
    slug: string;
    title: string;
    publishDate: string;
    breadcrumbs: {
      url: string;
      title: string;
    }[];
  };
  readonly location: PageProps["location"];
}

const PageTemplate: React.FC<Props> = ({ data, location, ...props }) => {
  const pageData =
    data.kontentItemHomepage?.elements || data.kontentItemPage?.elements;
  const pageContext = {
    slug: pageData.url?.url,
    title: pageData.title?.value,
    publishDate: pageData?.display_publish_date?.value,
    breadcrumbs: pageData?.breadcrumbs,
  };

  const elements = pageData.content.value[0].elements || {};
  const systemData = pageData.content.value[0].system || {};
  const defaultMetadata = useHomePageMetadata();
  const pageTitle =
    elements?.page_metadata__seo_title?.value || pageContext?.title || "";

  const pageDescription =
    elements?.page_metadata__page_description?.value ||
    defaultMetadata.pageDescription;
  const pageBgColor = getColor(elements?.page_metadata__background_color);
  const [pageMetadata, setPageMetadata] = useState({
    slug: pageContext.slug,
    location: location,
    breadcrumbs: pageContext.breadcrumbs,
  });

  const header: HeaderProps = {
    logoId: "short",
  };

  const layoutStyles: React.CSSProperties = {};
  if (pageBgColor) {
    layoutStyles.backgroundColor = pageBgColor;
  }

  const seo: SeoProps = {
    pageTitle: pageTitle,
    pageUrl:
      "https://www.lindahall.org" +
      (location.pathname === "/" ? "" : location.pathname),
    pageDescription: pageDescription,
    canonicalUrl: elements?.page_metadata__canonical_url?.value,
    robotsNoIndex:
      getMultiChoice(elements?.page_metadata__robots_index) === "noindex",
    robotsNoFollow:
      getMultiChoice(elements?.page_metadata__robots_follow) === "nofollow",
    sitemapInclude:
      getMultiChoice(elements?.page_metadata__sitemap_include) != "exclude",
    socialShareImage:
      elements?.page_metadata__social_share_image?.value[0] ||
      defaultMetadata.socialShareImage,
    socialShareTitle:
      elements?.page_metadata__social_share_title?.value ||
      pageTitle ||
      defaultMetadata.socialShareTitle,
    socialShareDescription:
      elements?.page_metadata__social_share_description?.value ||
      elements?.page_metadata__page_description?.value ||
      defaultMetadata.socialShareDescription,
    twitterCardType: getMultiChoice(elements?.page_metadata__twitter_card_type),
    lastModified: systemData.last_modified,
    publishDate: pageContext.publishDate,
    breadcrumbs: pageContext.breadcrumbs || [],
  };

  return (
    <MainLayout header={header} seo={seo} mainStyles={layoutStyles}>
      <PageMetadataContext.Provider value={pageMetadata}>
        <PageComponents systemId={systemData?.id} elements={elements} />
      </PageMetadataContext.Provider>
    </MainLayout>
  );
};

export default PageTemplate;

export interface PageQueryData {
  kontentItemHomepage: {
    system: {
      id: string;
    };
    preferred_language: string;
    internal: {
      type: string;
    };
    elements: PageDataElements;
  };
  kontentItemPage: {
    system: {
      id: string;
    };
    preferred_language: string;
    internal: {
      type: string;
    };
    elements: PageDataElements;
  };
}

export interface PageDataElements {
  title: {
    value: string;
  };
  url?: {
    value: string;
    url: string;
  };
  breadcrumbs?: {
    url: string;
    title: string;
  }[];
  display_publish_date: {
    value: string;
  };
  content: {
    value: {
      elements: KontentPageMetadata & {
        content_components__content: KontentRichText;
      };
      system: {
        id: string;
        last_modified: string;
      };
    }[];
  };
  kontentItemSiteFrame: KontentSiteFrame;
}

export const pageQuery = graphql`
  query PageById($id: String!) {
    kontentItemHomepage(id: { eq: $id }) {
      system {
        id
      }
      preferred_language
      internal {
        type
      }
      elements {
        title {
          value
        }
        content {
          ...pageContent
        }
      }
    }
    kontentItemPage(id: { eq: $id }) {
      system {
        id
      }
      preferred_language
      internal {
        type
      }
      elements {
        title {
          value
        }
        url {
          value
          url
        }
        breadcrumbs
        display_publish_date {
          value
        }
        content {
          ...pageContent
        }
      }
    }
  }
  fragment pageContent on kontent_item_modular_content_element_value {
    value {
      ... on kontent_item_content___page {
        id
        internal {
          type
        }
        system {
          id
        }
        elements {
          page_metadata__seo_title {
            value
          }
          page_metadata__page_description {
            value
          }
          page_metadata__canonical_url {
            value
          }
          page_metadata__relevancy_tags {
            value {
              codename
            }
          }
          page_metadata__robots_follow {
            value {
              codename
            }
          }
          page_metadata__robots_index {
            value {
              codename
            }
          }
          page_metadata__sitemap_include {
            value {
              codename
            }
          }
          page_metadata__social_share_description {
            value
          }
          page_metadata__social_share_image {
            value {
              ... on kontent_item_media___image {
                elements {
                  alt_text {
                    value
                  }
                  image {
                    value {
                      url
                      width
                      height
                    }
                  }
                }
              }
            }
          }
          page_metadata__social_share_title {
            value
          }
          page_metadata__twitter_card_type {
            value {
              codename
            }
          }
          page_metadata__background_color {
            ...brandColor
          }
          content_components__content {
            ...kontentRichText
          }
        }
      }
    }
  }
`;
